/* eslint-disable react-hooks/exhaustive-deps */
import { isBefore, isAfter } from "date-fns";
import Dexie from "dexie";
import { useLiveQuery } from "dexie-react-hooks";
import { useEffect, useMemo, useState } from "react";
import { db } from "~/db/db";
import useWorker from "~hooks/useWorker";
import useVendedor from "~/fetchs/useVendedor";

const useTabelaSolicitacoes = ({ filters, skip, take, setLoading, negociacoes, solicitacoes, type, clienteId, setLoadingSearch }) => {
  const [clientesDisponiveis, setClientesDisponiveis] = useState([]);
  const [total, setTotal] = useState(0);

  const { clientesSelecionados } = useVendedor();

  const fixNegociacoes = useMemo(() => {
    return (negociacoes || []).map(e => {
      const produtos = {};
      (e.grupoProdutos || []).forEach(produto => {
        let porcentagem = [];
        let produtoLabel = produto.produtoLabel || "";

        switch (e.tipoCondicaoLabel) {
          case "Condição Especial":
            if (produto?.descontoCif != 0)
              porcentagem.push(`CIF(${produto?.descontoCif ? produto?.descontoCif?.toFixed(2) : e?.descontoCif?.toFixed(2) }%)`)

            if (produto?.descontoFob != 0)
              porcentagem.push(`FOB(${produto?.descontoFob ? produto?.descontoFob?.toFixed(2) :  e?.descontoFob?.toFixed(2)}%)`)

            produtoLabel = produto.produtoLabel +
                        (e.filial?.nome ? ` - ${e.filial.nome}` : "") +
                        `${porcentagem.length > 0 ? " (" + porcentagem.join(" | ") + ") " : ""}`;
            break;
          case "Faixa":
            porcentagem.push(`${produto?.descontoFob ? produto?.descontoFob?.toFixed(2) : e?.descontoFob?.toFixed(2)}%`)
            produtoLabel = produto.produtoLabel + `${porcentagem.length > 0 ? " (" + porcentagem.join(" | ") + ") " : ""}`;
            break;
        }

        if (!(produto.clienteLabel in produtos))
          produtos[produto.clienteLabel] = [];

        produtos[produto.clienteLabel].push({
          id: produto.produtoId,
          grupoProdutoId: produto.grupoProdutoId,
          filialId: produto.filialId,
          produtoLabel: produtoLabel
        });
      });

      return {
        tipo: "tabela-preco",
        codigo: e.codigoExterno ?? e.id.toString().padStart(7, "0"),
        status: e.statusNegociacaoLabel,
        motivo: e.motivo?.descricao,
        grupoProdutoId: e.grupoProdutos[0]?.grupoProdutoId,
        grupoProduto: e.grupoProdutos[0]?.produtoLabel,
        grupoProdutoLabel: e.grupoProdutos[0]?.produtoLabel,
        produtosFull:e.grupoProdutos,
        origem: e.origemNegociacaoLabel,
        tipoCondicaoId: e.tipoCondicaoId,
        tipoCondicao: e.tipoCondicaoLabel,
        tipoCondicaoLabel: e.tipoCondicaoLabel,
        id: `tabela-preco-${e.id}`,
        originalId: e.id,
        filialId: e.filialId,
        filial: e.filial?.nome,
        produtos: produtos,
        clienteId: e.clienteId,
        razaoSocial: e.clienteLabel,
        clienteLabel: e.clienteLabel,
        data: new Date(e.createdAt).toISOString(),
        createdAt: e.createdAt,
        observacao: e.stepsObservacao || "NA",
        steps: (e.steps || [])
      }
    }
    )
  }, [negociacoes]);

  const fixSolicitacoes = useMemo(() => {
    return (solicitacoes || []).map(e => {
      const produtos = {};
      (e.produtos || []).forEach(c => {
        let porcentagem = [];
        switch (c.tipoCondicao) {
          case "Condição Especial":
            if (c.descontoCif != 0)
              porcentagem.push(`CIF(${c.descontoCif}%)`)

            if (c.descontoFob != 0)
              porcentagem.push(`FOB(${c.descontoFob}%)`)
            break;
          case "Faixa":
            porcentagem.push(`${c.descontoFob.toFixed(2)}%`)
            break;
        }

        if (!(c.clienteLabel in produtos))
          produtos[c.clienteLabel] = [];

        produtos[c.clienteLabel].push({
          ...c,
          grupoProdutoId: c.grupoMaterialId,
          produtoLabel: `${c.codigo} - ${c.nome} - ${c.filial} ${porcentagem.length > 0 ? " (" + porcentagem.join(" | ") + ") " : ""}`
        });
      });

      return {
        id: `simulador-${e.id}`,
        tipo: "simulador",
        origem: "Simulador",
        tipoCondicaoId: e.tipoCondicaoId,
        tipoCondicao: e.tipoCondicao,
        tipoCondicaoLabel: e.tipoCondicao,
        status: e.statusDescricao,
        motivo: e.motivo,
        observacao: e.stepsObservacao || "NA",
        steps: e.steps,
        data: new Date(e.dataSolicitacao).toISOString(),
        createdAt: e.dataSolicitacao,
        codigo: e.codigoExterno,
        produtos: produtos,
        clienteId: e.clienteId,
        razaoSocial: e.clienteRazaoSocial,
        clienteLabel: e.clienteRazaoSocial,
        originalId: e.simulacaoId
      }
    });
  }, [solicitacoes]);

  useEffect(() => {
    const items = fixNegociacoes.concat(fixSolicitacoes)
    if (items?.length) {
      db.tabelaSolicitacoes.bulkPut(items);
    }
  }, [fixNegociacoes, fixSolicitacoes]);

  const getTotal = async () => {
    const val = await tabelaSolicitacoesQuery(false)?.length;
    setTotal(val);
  };

  const tabelaSolicitacoesQuery = async (withOrdem = true) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let startDate = new Date(today);

    if (filters.periodo === "last30days") {
      startDate.setDate(today.getDate() - 30);
    } else if (filters.periodo === "last15days") {
      startDate.setDate(today.getDate() - 15);
    } else if (filters.periodo === "last7days") {
      startDate.setDate(today.getDate() - 7);
    } else if (filters.periodo === "last60days") {
      startDate.setDate(today.getDate() - 60);
    } else if (filters.periodo === "last90days") {
      startDate.setDate(today.getDate() - 90);
    } else if (filters.periodo === "last5days") {
      startDate.setDate(today.getDate() - 5);
    } else if (filters.periodo === "last3days") {
      startDate.setDate(today.getDate() - 3);
    } else if (filters.periodo === "today") {
      startDate.setDate(today.getDate());
    }

    let query = db.tabelaSolicitacoes;

    const statusMapping = {
      pendente: ['Pendente'],
      aprovado: ['Concluido'],
      recusado: ['Recusado', 'Rejeitado']
    };

    const notIn = (value) => ![0, -1].includes(value);
    const id = clienteId ? Number(clienteId) : (filters.cliente && filters.cliente !== 0 ? Number(filters.cliente) : 0);

    query = query.filter(negociacao => {
      const produtosValues = Object.values(negociacao.produtos || {});

      return (
        (id && notIn(id)
          ? negociacao.clienteId === id
          : true) &&
        (filters.grupoProduto && notIn(filters.grupoProduto)
          ? produtosValues.some(arr =>
              arr.some(c => c.grupoProdutoId == filters.grupoProduto))
          : true) &&
        (filters.produto && notIn(filters.produto)
          ? produtosValues.some(arr =>
              arr.some(c => c.id == filters.produto))
          : true) &&
        (filters.filial && notIn(filters.filial)
          ? produtosValues.some(arr =>
              arr.some(c => c.filialId == filters.filial))
          : true) &&
        (filters.tipoSolicitacao && notIn(filters.tipoSolicitacao)
          ? negociacao.tipoCondicaoId == filters.tipoSolicitacao
          : true) &&
        (filters.statusSolicitacao && notIn(filters.statusSolicitacao)
          ? statusMapping[filters.statusSolicitacao]?.includes(negociacao.status)
          : true) &&
        (filters.periodo === "personalizado"
          ? isAfter(new Date(negociacao.createdAt), new Date(filters.startDate)) &&
            isBefore(new Date(negociacao.createdAt), new Date(filters.endDate))
          : filters.periodo
          ? isBefore(startDate, new Date(negociacao.createdAt))
          : true)
      );
    });

    if (clientesSelecionados) {
      query = query.filter(x => clientesSelecionados.includes(x.clienteId));
    }

    return query;
  };


  const solicitacoesQuery = useLiveQuery(async () => {
    let query = await tabelaSolicitacoesQuery();

    let sortedQuery;
    if (filters?.ordem === "recentes") {
      sortedQuery = await query.sortBy('createdAt');
      sortedQuery.reverse();
    } else {
      sortedQuery = await query.sortBy('createdAt');
    }

    return sortedQuery;
  }, [filters, skip, clientesSelecionados]);

  const solicitacoesQueryAll = useLiveQuery(async () => {
    const query = await tabelaSolicitacoesQuery();
    const arr = [];
    await query.each(e => arr.push(e));
    if (typeof setLoading === "function") setLoading(false);
    return arr;
  }, [filters, clienteId, clientesSelecionados]) ?? [];

  useEffect(() => {
    const fetchClientesDisponiveis = async () => {
      const clienteIds = new Set();
      await db.tabelaSolicitacoes.each((solicitacao) => {
        clienteIds.add(solicitacao.clienteId);
      });
      const uniqueClienteIds = Array.from(clienteIds);

      setClientesDisponiveis(Array.from(uniqueClienteIds));
    };
    fetchClientesDisponiveis();
    getTotal()
  }, [filters]);

  const totalRenderizado = useMemo(() => {
    if (solicitacoesQuery) {
      if (typeof setLoading == "function") setLoading(false);
      return solicitacoesQuery.length;
    }
    return 0;
  }, [solicitacoesQuery]);

  useEffect(() => {



  }, [filters]);

  const { save } = useWorker({
    table: "tabelaSolicitacoes",
  });

  return {
    clientesDisponiveis,
    solicitacoesQuery,
    totalRenderizado,
    total,
    fixNegociacoes,
    solicitacoesQueryAll
  };
}

export default useTabelaSolicitacoes;